/**
 * Aging Report Service
 *
 * API client for the AR/AP aging report endpoint.
 */

import { get, buildQueryString, type ApiResponse } from './api-client'

// ============================================================================
// Types
// ============================================================================

export interface AgingBucketTotal {
  label: string
  daysFrom: number
  daysTo: number
  amount: number
  currency: string
  invoiceCount: number
}

export interface AgingInvoice {
  invoiceId: string
  invoiceNumber: string
  invoiceDate: string
  dueDate: string
  daysOverdue: number
  totalAmount: number
  paidAmount: number
  outstandingAmount: number
  currency: string
  bucketLabel: string
}

export interface AgingPartyRow {
  partyId: string
  partyName: string
  partyCode: string
  bucketAmounts: number[]
  totalOutstanding: number
  currency: string
  invoices: AgingInvoice[]
}

export interface AgingReportResponse {
  asOfDate: string
  generatedAt: string
  reportType: string
  agingBasis: 'due_date' | 'bill_date'
  bucketTotals: AgingBucketTotal[]
  parties: AgingPartyRow[]
  grandTotal: number
  currency: string
}

// ============================================================================
// API Functions
// ============================================================================

/** Get aging report (AR or AP). */
export async function getAgingReport(
  orgId: string,
  reportType: 'ar' | 'ap' = 'ar',
  asOfDate?: string,
  agingBasis: 'due_date' | 'bill_date' = 'due_date'
): Promise<ApiResponse<AgingReportResponse>> {
  const query = buildQueryString({
    report_type: reportType,
    as_of_date: asOfDate,
    aging_basis: agingBasis,
  })
  return get<AgingReportResponse>(`/organizations/${orgId}/reports/aging${query}`)
}

export interface PartyOutstandingResponse {
  partyId: string
  totalOutstanding: number
  currency: string
}

/** Get the total outstanding balance for a single party. */
export async function getPartyOutstanding(
  orgId: string,
  partyId: string
): Promise<ApiResponse<PartyOutstandingResponse>> {
  return get<PartyOutstandingResponse>(`/organizations/${orgId}/parties/${partyId}/outstanding`)
}
